import React, { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EntryTypeIcon from '../common/EntryTypeIcon';
import { formatText, formatTextForDatabase } from '../common/TextFormatterProvider';
import { prettifyEntryKey } from '../../utils/widgetModel';
import { getVisibleOutputTypes, getOutputMeta, isEmailFeatureEnabled } from '../../utils/outputRegistry';

const bindingId = (sourceBlockId, entryKey) => `${sourceBlockId}::${entryKey}`;

// Email content limits (section 3.3 of the email_send spec). SUBJECT_MAX is the
// length of the CONFIGURED subject, before the server composes the final
// `From {company}: {subject}` header; COMPOSED_SUBJECT_MAX bounds that composed
// header, so a long company name is caught here rather than at run time. The
// body is plain text and, unlike the subject, may contain line breaks.
const SUBJECT_MAX = 160;
const COMPOSED_SUBJECT_MAX = 200;
const BODY_MAX = 20000;

const composedSubject = (companyDisplayName, subject) => `From ${companyDisplayName}: ${subject}`;
// A soft ceiling that warns the user; the backend enforces the hard limit.
const ATTACHMENT_WARN_BYTES = 10 * 1024 * 1024; // 10 MiB
// The most attachments one email may select — the runtime's limit
// (outbound_email MAX_SELECTED_ATTACHMENTS), enforced here so a published
// function never fails only when it runs.
const MAX_ATTACHMENTS = 20;

// The one mount-name rule, derived from the source node alone (H25 v2):
// `{block_id}-{table_id}` for a data source, the source block id otherwise.
// `edge.meta.targetAlias` is retired and ignored everywhere.
const getSourceAlias = (sourceNode, blockConfigs) => {
  const sourceBlockId = sourceNode.data?.blockId || sourceNode.id;
  if (sourceNode.data?.type === 'data_source') {
    const tableId = blockConfigs[sourceBlockId]?.config?.table_id;
    if (tableId) return `${sourceBlockId}-${tableId}`;
  }
  return sourceBlockId;
};

// The entries an Output can bind, grouped by the Logic or Predefined block
// directly feeding it. Both kinds of block carry the same `artifacts_manifest`
// — a Logic block's from its implement, a Predefined block's from its preview
// — so an Output reads one shape whichever made it.
export const buildOutputSourceGroups = ({
  nodes,
  edges,
  outputNode,
  blockConfigs,
}) => {
  if (!outputNode) return [];

  const groupsByBlockId = new Map();
  edges
    .filter((edge) => edge.target === outputNode.id)
    .forEach((edge) => {
      const sourceNode = nodes.find((node) => node.id === edge.source);
      const sourceType = sourceNode?.data?.type;
      if (!sourceNode || !['logic', 'predefined'].includes(sourceType)) return;

      const sourceBlockId = sourceNode.data.blockId;
      if (!sourceBlockId || groupsByBlockId.has(sourceBlockId)) return;

      const blockConfig = blockConfigs[sourceBlockId] || {};
      groupsByBlockId.set(sourceBlockId, {
        sourceBlockId,
        sourceType,
        name: formatText(blockConfig.name || sourceNode.data?.config?.name || 'Unnamed block'),
        alias: getSourceAlias(sourceNode, blockConfigs),
        entries: blockConfig.artifacts_manifest || [],
      });
    });

  return Array.from(groupsByBlockId.values());
};

export const outputBindingIsUnavailable = (binding, groups) => {
  const group = groups.find(candidate => candidate.sourceBlockId === binding.source_block_id);
  if (!group) return true;
  return !group.entries.some(entry => entry.key === binding.entry_key);
};

// Attach a stable human label to each source group. When two connected nodes
// share a name, append a topological ordinal (`Build report (1)`, `Build report
// (2)`) so the user can tell them apart; the persisted binding stays the
// block-id/key pair (section 1.2). Pure and exported for unit testing.
export const assignSourceGroupDisplayNames = (groups) => {
  const totals = {};
  (groups || []).forEach((group) => {
    totals[group.name] = (totals[group.name] || 0) + 1;
  });
  const seen = {};
  return (groups || []).map((group) => {
    seen[group.name] = (seen[group.name] || 0) + 1;
    const displayName = totals[group.name] > 1
      ? `${group.name} (${seen[group.name]})`
      : group.name;
    return { ...group, displayName };
  });
};

const getEntryLabel = (entry) => {
  if (entry.kind === 'data') return entry.description || prettifyEntryKey(entry.key) || 'Data';
  if (entry.kind === 'chart') return entry.title || prettifyEntryKey(entry.key);
  if (entry.kind === 'folder') {
    const fileCount = entry.files_at_build?.length || 0;
    return `${prettifyEntryKey(entry.key)} (${fileCount} ${fileCount === 1 ? 'file' : 'files'})`;
  }
  return entry.filename || prettifyEntryKey(entry.key);
};

// Best-effort known byte size of a manifest entry. `data` and `chart` are
// materialised at run time and have no known size here; a `folder` sums the
// sizes of the files known at build time.
const getEntryBytes = (entry) => {
  if (!entry) return null;
  if (entry.kind === 'file') return Number.isFinite(entry.size_bytes) ? entry.size_bytes : null;
  if (entry.kind === 'folder') {
    const files = entry.files_at_build || [];
    const known = files
      .map((file) => file.size_bytes)
      .filter((size) => Number.isFinite(size));
    if (known.length === 0) return null;
    return known.reduce((total, size) => total + size, 0);
  }
  return null;
};

const formatBytes = (value) => {
  if (!Number.isFinite(value)) return null;
  if (value < 1024) return `${value} B`;
  if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 ** 2)).toFixed(1)} MB`;
};

const contactStatusOf = (contact) => contact?.status || 'active';

const contactDisplay = (contact) => {
  if (!contact) return null;
  const name = contact.display_name || contact.email || contact.id;
  return contact.email && contact.email !== name ? `${name} <${contact.email}>` : name;
};

// An unnamed Output shows its destination's label ("Decode UI", "Email"), so
// the canvas never calls an Email node "Decode UI".
const defaultOutputName = (outputType) => getOutputMeta(outputType)?.label || 'Output';

const OutputBlockConfig = forwardRef(function OutputBlockConfig({
  blockConfig = {},
  nodes = [],
  edges = [],
  outputNode,
  blockConfigs = {},
  contacts = [],
  companyDisplayName = '',
  onSave,
  onCancel,
}, ref) {
  const [name, setName] = useState('');
  const [outputType, setOutputType] = useState('decode_ui');
  const [bindings, setBindings] = useState([]);
  // Email destination state.
  const [recipientContactIds, setRecipientContactIds] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState('');
  const loadedBlockIdRef = useRef(null);

  const emailFeatureEnabled = isEmailFeatureEnabled();

  useEffect(() => {
    const currentBlockId = outputNode?.data?.blockId;
    if (!currentBlockId || loadedBlockIdRef.current === currentBlockId || !blockConfig.id) return;
    loadedBlockIdRef.current = currentBlockId;
    const config = blockConfig.config || {};
    const storedType = config.output_type || 'decode_ui';
    setName(formatText(blockConfig.name || defaultOutputName(storedType)));
    setOutputType(storedType);
    setBindings(Array.isArray(config.bindings) ? config.bindings : []);
    setRecipientContactIds(Array.isArray(config.recipient_contact_ids) ? config.recipient_contact_ids : []);
    setSubject(config.subject || '');
    setBody(config.body || '');
    setAttachments(Array.isArray(config.attachments) ? config.attachments : []);
    setLoadedGeneration((n) => n + 1);
  }, [blockConfig, outputNode]);

  const sourceGroups = useMemo(() => assignSourceGroupDisplayNames(buildOutputSourceGroups({
    nodes,
    edges,
    outputNode,
    blockConfigs,
  })), [nodes, edges, outputNode, blockConfigs]);

  const availableEntries = useMemo(() => {
    const entries = new Map();
    sourceGroups.forEach((group) => {
      group.entries.forEach((entry) => {
        entries.set(bindingId(group.sourceBlockId, entry.key), { group, entry });
      });
    });
    return entries;
  }, [sourceGroups]);

  const contactsById = useMemo(() => {
    const map = new Map();
    (contacts || []).forEach((contact) => {
      map.set(contact.id || contact.contact_id, contact);
    });
    return map;
  }, [contacts]);

  const activeContacts = useMemo(
    () => (contacts || [])
      .filter((contact) => contactStatusOf(contact) === 'active')
      .sort((a, b) => String(a.display_name || a.email || '').localeCompare(String(b.display_name || b.email || ''))),
    [contacts],
  );

  const inactiveSelectedContacts = useMemo(
    () => recipientContactIds
      .map((id) => contactsById.get(id))
      .filter((contact) => contact && contactStatusOf(contact) !== 'active'),
    [recipientContactIds, contactsById],
  );

  const missingRecipientIds = useMemo(
    () => recipientContactIds.filter((id) => !contactsById.has(id)),
    [recipientContactIds, contactsById],
  );

  // --- Decode UI binding helpers (unchanged behaviour) ----------------------
  const selectedBindingIds = useMemo(
    () => new Set(bindings.map((binding) => bindingId(binding.source_block_id, binding.entry_key))),
    [bindings],
  );

  const staleBindings = useMemo(
    () => bindings.filter(binding => outputBindingIsUnavailable(binding, sourceGroups)),
    [bindings, sourceGroups],
  );

  const toggleBinding = (sourceBlockId, entryKey) => {
    const id = bindingId(sourceBlockId, entryKey);
    setBindings((currentBindings) => {
      if (currentBindings.some((binding) => bindingId(binding.source_block_id, binding.entry_key) === id)) {
        return currentBindings.filter((binding) => bindingId(binding.source_block_id, binding.entry_key) !== id);
      }
      return [...currentBindings, { source_block_id: sourceBlockId, entry_key: entryKey }];
    });
    setError('');
  };

  const moveBinding = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= bindings.length) return;
    setBindings((currentBindings) => {
      const orderedBindings = [...currentBindings];
      [orderedBindings[index], orderedBindings[nextIndex]] = [orderedBindings[nextIndex], orderedBindings[index]];
      return orderedBindings;
    });
  };

  // --- Email attachment helpers ---------------------------------------------
  const selectedAttachmentIds = useMemo(
    () => new Set(attachments.map((entry) => bindingId(entry.source_block_id, entry.entry_key))),
    [attachments],
  );

  const staleAttachments = useMemo(
    () => attachments.filter(entry => outputBindingIsUnavailable(entry, sourceGroups)),
    [attachments, sourceGroups],
  );

  const attachmentSummary = useMemo(() => {
    let knownBytes = 0;
    let unknownCount = 0;
    attachments.forEach((entry) => {
      const resolved = availableEntries.get(bindingId(entry.source_block_id, entry.entry_key));
      const bytes = getEntryBytes(resolved?.entry);
      if (Number.isFinite(bytes)) knownBytes += bytes;
      else unknownCount += 1;
    });
    return { knownBytes, unknownCount };
  }, [attachments, availableEntries]);

  const toggleAttachment = (sourceBlockId, entryKey) => {
    const id = bindingId(sourceBlockId, entryKey);
    setAttachments((current) => {
      if (current.some((entry) => bindingId(entry.source_block_id, entry.entry_key) === id)) {
        return current.filter((entry) => bindingId(entry.source_block_id, entry.entry_key) !== id);
      }
      return [...current, { source_block_id: sourceBlockId, entry_key: entryKey }];
    });
    setError('');
  };

  const handleDestinationChange = (nextType) => {
    // Carry the default name across destinations, but never overwrite a name
    // the user chose themselves.
    setName((current) => (
      !current.trim() || current === defaultOutputName(outputType)
        ? defaultOutputName(nextType)
        : current
    ));
    setOutputType(nextType);
    setError('');
    // Do not carry stale configuration across destinations (section 4.7 item 5).
    if (nextType === 'email') {
      setBindings([]);
    } else {
      setRecipientContactIds([]);
      setSubject('');
      setBody('');
      setAttachments([]);
    }
  };

  // Reconcile a change from the active-contacts multi-select while preserving any
  // already-referenced inactive/missing recipients. Those are kept until the user
  // removes them explicitly: hiding them and promising removal on save would be a
  // lie, because saving with them present is rejected.
  const handleRecipientsChange = (nextActiveIds) => {
    const activeIdSet = new Set(activeContacts.map((contact) => contact.id || contact.contact_id));
    const next = Array.isArray(nextActiveIds) ? nextActiveIds : [];
    setRecipientContactIds((current) => {
      const preserved = current.filter((id) => !activeIdSet.has(id));
      return [...next, ...preserved];
    });
    setError('');
  };

  const handleRemoveRecipient = (contactId) => {
    setRecipientContactIds((current) => current.filter((id) => id !== contactId));
    setError('');
  };

  // Destination options: the visible registry types, plus the currently stored
  // type if the feature flag has since been turned off (so an existing Email
  // Output still renders rather than silently losing its destination).
  const destinationOptions = useMemo(() => {
    const options = getVisibleOutputTypes().map((meta) => meta.type);
    if (outputType && !options.includes(outputType)) options.push(outputType);
    return options;
  }, [outputType]);

  const emailReadOnly = outputType === 'email' && !emailFeatureEnabled;

  const handleSave = () => {
    if (!name.trim()) {
      setError('Please provide a name for the output block.');
      return false;
    }

    if (outputType === 'decode_ui') {
      if (bindings.length === 0) {
        setError('Select at least one output entry.');
        return false;
      }
      if (staleBindings.length > 0) {
        setError('Remove unavailable entries before saving the output block.');
        return false;
      }
      return onSave({
        name: formatTextForDatabase(name.trim()),
        config: {
          output_type: 'decode_ui',
          bindings,
        },
      });
    }

    if (outputType === 'email') {
      if (emailReadOnly) {
        setError('Email sending is currently disabled. This output cannot be edited.');
        return false;
      }
      const uniqueRecipients = Array.from(new Set(recipientContactIds));
      if (uniqueRecipients.length === 0) {
        setError('Select at least one recipient.');
        return false;
      }
      if (uniqueRecipients.length > 20) {
        setError('An email output can have at most 20 recipients.');
        return false;
      }
      const invalidRecipient = uniqueRecipients.find((id) => {
        const contact = contactsById.get(id);
        return !contact || contactStatusOf(contact) !== 'active';
      });
      if (invalidRecipient) {
        setError('Remove suppressed, deleted, or unknown recipients before saving.');
        return false;
      }
      const trimmedSubject = subject.trim();
      if (!trimmedSubject) {
        setError('Please enter an email subject.');
        return false;
      }
      if (trimmedSubject.length > SUBJECT_MAX) {
        setError(`The subject must be ${SUBJECT_MAX} characters or fewer.`);
        return false;
      }
      if (composedSubject(subjectPreviewCompany, trimmedSubject).length > COMPOSED_SUBJECT_MAX) {
        setError(`Recipients see "From ${subjectPreviewCompany}: …", which must be ${COMPOSED_SUBJECT_MAX} characters or fewer. Shorten the subject.`);
        return false;
      }
      if (/[\r\n]/.test(trimmedSubject)) {
        setError('The subject must not contain line breaks.');
        return false;
      }
      if (!body.trim()) {
        setError('Please enter an email body.');
        return false;
      }
      if (body.length > BODY_MAX) {
        setError(`The body must be ${BODY_MAX.toLocaleString()} characters or fewer.`);
        return false;
      }
      if (attachments.length === 0) {
        setError('Select at least one attachment to send.');
        return false;
      }
      if (staleAttachments.length > 0) {
        setError('Remove unavailable attachments before saving the output block.');
        return false;
      }
      if (attachments.length > MAX_ATTACHMENTS) {
        setError(`An email can carry at most ${MAX_ATTACHMENTS} attachments — ${attachments.length} are selected.`);
        return false;
      }
      return onSave({
        name: formatTextForDatabase(name.trim()),
        config: {
          output_type: 'email',
          recipient_contact_ids: uniqueRecipients,
          subject: trimmedSubject,
          body,
          attachments,
        },
      });
    }

    setError('This output destination is not supported.');
  };

  // The builder's Publish and Close ask this editor two things through its ref:
  // whether what is on screen differs from what was loaded, and to save it.
  // The baseline is the editable state as it stood when loading finished.
  const [loadedGeneration, setLoadedGeneration] = useState(0);
  const baselineRef = useRef('');
  const editSignature = JSON.stringify({ name, outputType, bindings, recipientContactIds, subject, body, attachments });
  useEffect(() => {
    baselineRef.current = editSignature;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedGeneration]);
  useImperativeHandle(ref, () => ({
    isDirty: () => editSignature !== baselineRef.current,
    save: () => Promise.resolve(handleSave()),
  }));

  const subjectPreviewCompany = companyDisplayName || 'Your company';

  return (
    <Box sx={{
      p: 2,
      width: '720px',
      height: '82vh',
      position: 'absolute',
      top: 100,
      left: '3.3%',
      zIndex: 999,
      background: '#1A1E26',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderTop: '3px solid #F0B25A',
      borderRadius: 1,
      color: '#F0F2F5',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Grid container spacing={2} sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <Grid item xs={12} sx={{ maxHeight: '76vh', overflowY: 'auto' }}>
          <Typography variant="h6" sx={{ color: '#F0B25A', mb: 2 }}>
            Configure Output Block
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {emailReadOnly && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Email sending is currently disabled for this workspace. This existing Email output is shown read-only.
            </Alert>
          )}

          <TextField
            fullWidth
            size="small"
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth size="small" sx={{ mb: 3 }}>
            <InputLabel id="output-destination-label">Destination</InputLabel>
            <Select
              labelId="output-destination-label"
              value={outputType}
              label="Destination"
              onChange={(event) => handleDestinationChange(event.target.value)}
            >
              {destinationOptions.map((type) => (
                <MenuItem key={type} value={type}>
                  {getOutputMeta(type)?.label || type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {outputType === 'email' ? (
            <EmailEditor
              activeContacts={activeContacts}
              recipientContactIds={recipientContactIds}
              inactiveSelectedContacts={inactiveSelectedContacts}
              missingRecipientIds={missingRecipientIds}
              onRecipientsChange={handleRecipientsChange}
              onRemoveRecipient={handleRemoveRecipient}
              disabled={emailReadOnly}
              subject={subject}
              onSubjectChange={setSubject}
              subjectPreviewCompany={subjectPreviewCompany}
              body={body}
              onBodyChange={setBody}
              sourceGroups={sourceGroups}
              selectedAttachmentIds={selectedAttachmentIds}
              onToggleAttachment={toggleAttachment}
              attachments={attachments}
              staleAttachments={staleAttachments}
              attachmentSummary={attachmentSummary}
              availableEntries={availableEntries}
              blockConfigs={blockConfigs}
            />
          ) : (
            <DecodeUiEditor
              sourceGroups={sourceGroups}
              selectedBindingIds={selectedBindingIds}
              onToggleBinding={toggleBinding}
              bindings={bindings}
              availableEntries={availableEntries}
              staleBindings={staleBindings}
              onMoveBinding={moveBinding}
              blockConfigs={blockConfigs}
            />
          )}

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={emailReadOnly}
              sx={{ backgroundColor: '#F0B25A !important', color: '#111318' }}
            >
              Save
            </Button>
            <Button variant="text" onClick={onCancel} sx={{ color: '#ADB5BD' }}>Cancel</Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
});

// --- Decode UI destination editor (existing behaviour, extracted) -----------
function DecodeUiEditor({
  sourceGroups,
  selectedBindingIds,
  onToggleBinding,
  bindings,
  availableEntries,
  staleBindings,
  onMoveBinding,
  blockConfigs,
}) {
  return (
    <>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Available node outputs</Typography>
      {sourceGroups.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Connect a configured logic or predefined block to choose its outputs.
        </Alert>
      )}

      {sourceGroups.map((group) => (
        <Paper
          key={group.sourceBlockId}
          variant="outlined"
          sx={{ p: 1.5, mb: 1.5, bgcolor: 'rgba(255,255,255,0.02)' }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">{group.displayName}</Typography>
            <Chip size="small" label={`alias: ${group.alias}`} />
          </Stack>

          {group.entries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              This block has not produced a manifest yet.
            </Typography>
          ) : group.entries.map((entry) => (
            <FormControlLabel
              key={bindingId(group.sourceBlockId, entry.key)}
              sx={{ display: 'flex', mx: 0 }}
              control={(
                <Checkbox
                  checked={selectedBindingIds.has(bindingId(group.sourceBlockId, entry.key))}
                  onChange={() => onToggleBinding(group.sourceBlockId, entry.key)}
                />
              )}
              label={(
                <Stack direction="row" spacing={1} alignItems="center">
                  <EntryTypeIcon kind={entry.kind} filename={entry.filename} mime={entry.mime} />
                  <Typography variant="body2">{getEntryLabel(entry)}</Typography>
                  <Chip size="small" variant="outlined" label={entry.kind} />
                </Stack>
              )}
            />
          ))}
        </Paper>
      ))}

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Tab order</Typography>

      {staleBindings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {staleBindings.map((binding) => {
            const sourceName = formatText(blockConfigs[binding.source_block_id]?.name || binding.source_block_id);
            return (
              <Typography key={bindingId(binding.source_block_id, binding.entry_key)} variant="body2">
                Output references `{binding.entry_key}`, which {sourceName} no longer produces.
              </Typography>
            );
          })}
        </Alert>
      )}

      {bindings.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select entries above to build the Decode UI tab order.
        </Typography>
      ) : bindings.map((binding, index) => {
        const available = availableEntries.get(bindingId(binding.source_block_id, binding.entry_key));
        const unavailable = outputBindingIsUnavailable(binding, sourceGroups);
        return (
          <Paper
            key={bindingId(binding.source_block_id, binding.entry_key)}
            variant="outlined"
            sx={{ p: 1, mb: 1, borderColor: unavailable ? 'error.main' : undefined }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ flex: 1 }}>
                {index + 1}. {available?.group.displayName || binding.source_block_id} / {available
                  ? getEntryLabel(available.entry)
                  : `${binding.entry_key} (${unavailable ? 'unavailable' : 'catalogue unavailable'})`}
              </Typography>
              <IconButton
                size="small"
                disabled={index === 0}
                onClick={() => onMoveBinding(index, -1)}
                aria-label="Move output earlier"
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={index === bindings.length - 1}
                onClick={() => onMoveBinding(index, 1)}
                aria-label="Move output later"
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
              {unavailable && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => onToggleBinding(binding.source_block_id, binding.entry_key)}
                >
                  Remove
                </Button>
              )}
            </Stack>
          </Paper>
        );
      })}
    </>
  );
}

// --- Email destination editor ----------------------------------------------
function EmailEditor({
  activeContacts,
  recipientContactIds,
  inactiveSelectedContacts,
  missingRecipientIds,
  onRecipientsChange,
  onRemoveRecipient,
  disabled,
  subject,
  onSubjectChange,
  subjectPreviewCompany,
  body,
  onBodyChange,
  sourceGroups,
  selectedAttachmentIds,
  onToggleAttachment,
  attachments,
  staleAttachments,
  attachmentSummary,
  availableEntries,
  blockConfigs,
}) {
  const totalKnown = formatBytes(attachmentSummary.knownBytes);
  const overWarnLimit = attachmentSummary.knownBytes > ATTACHMENT_WARN_BYTES;
  // At the limit no further entry can be ticked; over it (a configuration saved
  // before the limit was enforced) the count reads as an error until reduced.
  const atAttachmentLimit = attachments.length >= MAX_ATTACHMENTS;
  const overAttachmentLimit = attachments.length > MAX_ATTACHMENTS;
  const composedPreview = composedSubject(subjectPreviewCompany, subject);
  const composedTooLong = composedPreview.length > COMPOSED_SUBJECT_MAX;

  return (
    <>
      {/* Recipients */}
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Recipients</Typography>
      {activeContacts.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No active contacts yet. Add contacts in{' '}
          <Link href="/settings?section=emailContacts" target="_blank" rel="noopener" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
            Settings &rsaquo; Email Contacts<OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
          {' '}before configuring an email output.
        </Alert>
      ) : (
        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
          <InputLabel id="email-recipients-label">Contacts</InputLabel>
          <Select
            labelId="email-recipients-label"
            multiple
            disabled={disabled}
            label="Contacts"
            value={recipientContactIds.filter((id) => activeContacts.some((c) => (c.id || c.contact_id) === id))}
            onChange={(event) => onRecipientsChange(event.target.value)}
            renderValue={(selected) => selected
              .map((id) => {
                const contact = activeContacts.find((c) => (c.id || c.contact_id) === id);
                return contact ? (contact.display_name || contact.email) : id;
              })
              .join(', ')}
          >
            {activeContacts.map((contact) => {
              const id = contact.id || contact.contact_id;
              return (
                <MenuItem key={id} value={id}>
                  <Checkbox checked={recipientContactIds.includes(id)} />
                  <ListItemText primary={contact.display_name || contact.email} secondary={contact.email} />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Recipients are chosen from your company contacts. Manage them in{' '}
        <Link href="/settings?section=emailContacts" target="_blank" rel="noopener">Settings &rsaquo; Email Contacts</Link>.
      </Typography>

      {(inactiveSelectedContacts.length > 0 || missingRecipientIds.length > 0) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            These recipients can no longer receive email. Remove them to save this output.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {inactiveSelectedContacts.map((contact) => {
              const id = contact.id || contact.contact_id;
              return (
                <Chip
                  key={id}
                  size="small"
                  color="warning"
                  variant="outlined"
                  disabled={disabled}
                  label={`${contactDisplay(contact)} · ${contactStatusOf(contact)}`}
                  onDelete={() => onRemoveRecipient(id)}
                />
              );
            })}
            {missingRecipientIds.map((id) => (
              <Chip
                key={id}
                size="small"
                color="warning"
                variant="outlined"
                disabled={disabled}
                label="Deleted contact"
                onDelete={() => onRemoveRecipient(id)}
              />
            ))}
          </Stack>
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Subject + body */}
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Message</Typography>
      <TextField
        fullWidth
        size="small"
        label="Subject"
        value={subject}
        disabled={disabled}
        onChange={(event) => onSubjectChange(event.target.value)}
        inputProps={{ maxLength: SUBJECT_MAX }}
        error={subject.length > SUBJECT_MAX || composedTooLong}
        helperText={`${subject.length}/${SUBJECT_MAX}`}
        sx={{ mb: 1 }}
      />
      <Typography
        variant="caption"
        color={composedTooLong ? 'error.main' : 'text.secondary'}
        sx={{ display: 'block', mb: 2 }}
      >
        Recipients see: <strong>From {subjectPreviewCompany}: {subject || '(subject)'}</strong>
        {' · '}{composedPreview.length}/{COMPOSED_SUBJECT_MAX}
        {composedTooLong ? ' — shorten the subject before saving.' : ''}
      </Typography>
      <TextField
        fullWidth
        multiline
        minRows={4}
        maxRows={12}
        label="Body"
        value={body}
        disabled={disabled}
        onChange={(event) => onBodyChange(event.target.value)}
        inputProps={{ maxLength: BODY_MAX }}
        helperText={`${body.length.toLocaleString()}/${BODY_MAX.toLocaleString()} · Plain text only`}
        sx={{ mb: 2 }}
      />

      <Divider sx={{ my: 2 }} />

      {/* Attachments */}
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Attachments</Typography>
      {attachments.filter(entry => !availableEntries.has(bindingId(entry.source_block_id, entry.entry_key))
        && !outputBindingIsUnavailable(entry, sourceGroups)).map(entry => (
          <Typography key={bindingId(entry.source_block_id, entry.entry_key)} variant="body2" sx={{ mb: 1 }}>
            {entry.entry_key} (selected; catalogue unavailable)
          </Typography>
        ))}
      {sourceGroups.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Connect a configured logic or predefined block to choose its outputs as attachments.
        </Alert>
      )}

      {sourceGroups.map((group) => (
        <Paper
          key={group.sourceBlockId}
          variant="outlined"
          sx={{ p: 1.5, mb: 1.5, bgcolor: 'rgba(255,255,255,0.02)' }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{group.displayName}</Typography>
          {group.entries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              This block has not produced a manifest yet.
            </Typography>
          ) : group.entries.map((entry) => {
            const bytes = getEntryBytes(entry);
            const sizeLabel = formatBytes(bytes);
            const checked = selectedAttachmentIds.has(bindingId(group.sourceBlockId, entry.key));
            return (
              <FormControlLabel
                key={bindingId(group.sourceBlockId, entry.key)}
                sx={{ display: 'flex', mx: 0 }}
                control={(
                  <Checkbox
                    checked={checked}
                    disabled={disabled || (atAttachmentLimit && !checked)}
                    onChange={() => onToggleAttachment(group.sourceBlockId, entry.key)}
                  />
                )}
                label={(
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EntryTypeIcon kind={entry.kind} filename={entry.filename} mime={entry.mime} />
                    <Typography variant="body2">{getEntryLabel(entry)}</Typography>
                    <Chip size="small" variant="outlined" label={entry.kind} />
                    {sizeLabel && <Chip size="small" label={sizeLabel} />}
                  </Stack>
                )}
              />
            );
          })}
        </Paper>
      ))}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        A selected folder is delivered as a single ZIP file, and a selected chart is rendered to a PNG by Decode.
      </Typography>

      {staleAttachments.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            These attachments are no longer produced. Remove them to save this output.
          </Typography>
          {staleAttachments.map((entry) => {
            const sourceName = formatText(blockConfigs[entry.source_block_id]?.name || entry.source_block_id);
            return (
              <Stack
                key={bindingId(entry.source_block_id, entry.entry_key)}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 0.5 }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Attachment references `{entry.entry_key}`, which {sourceName} no longer produces.
                </Typography>
                <Button
                  size="small"
                  color="error"
                  disabled={disabled}
                  onClick={() => onToggleAttachment(entry.source_block_id, entry.entry_key)}
                >
                  Remove
                </Button>
              </Stack>
            );
          })}
        </Alert>
      )}

      <Alert severity={overAttachmentLimit ? 'error' : (overWarnLimit ? 'warning' : 'info')} sx={{ mb: 1 }}>
        {attachments.length} of {MAX_ATTACHMENTS} attachment{attachments.length === 1 ? '' : 's'} selected
        {totalKnown ? ` · about ${totalKnown} known` : ''}
        {attachmentSummary.unknownCount > 0
          ? ` · ${attachmentSummary.unknownCount} sized at run time`
          : ''}
        {overWarnLimit ? ' · this may exceed the 10 MiB email limit.' : ''}
        {overAttachmentLimit ? ` · remove ${attachments.length - MAX_ATTACHMENTS} to save.` : ''}
      </Alert>
    </>
  );
}

export default OutputBlockConfig;
